import axios, { type AxiosError } from 'axios';
import {
  getSafeRoutePattern,
  getErrorName,
  getSanitizedStackTop,
  getSafeApiUrl,
} from './sanitization';

type ErrorSource = 'react' | 'runtime' | 'unhandledrejection' | 'api';

interface ReportErrorOptions {
  source: ErrorSource;
  error?: unknown;
  api?: {
    method?: string;
    url?: string;
    status?: number;
  };
}

interface ErrorReportingPayload {
  eventTime: string;
  serviceContext: {
    service: string;
    version: string;
  };
  message: string;
  context: {
    httpRequest?: {
      method?: string;
      url?: string;
      responseStatusCode?: number;
    };
    reportLocation?: {
      filePath: string;
      functionName: string;
      lineNumber: number;
    };
  };
}

// Configuration
const isEnabled =
  import.meta.env.PROD &&
  import.meta.env.VITE_GCP_ERROR_REPORTING_ENABLED !== 'false';
const serviceName =
  import.meta.env.VITE_GCP_ERROR_REPORTING_SERVICE?.trim() ||
  'resource-matrix-web';
const serviceVersion = import.meta.env.VITE_GIT_SHA || 'local';

// Errors are posted to a same-origin telemetry endpoint. The backend (server.mjs)
// holds GCP credentials and forwards to Error Reporting after a second sanitization
// pass. The browser never holds a GCP key and never calls Google directly.
const clientErrorsEndpoint =
  import.meta.env.VITE_CLIENT_ERRORS_ENDPOINT?.trim() ||
  '/telemetry/client-errors';

// Rate limiting: cap error reports to prevent spam (10 per minute).
let errorReportCount = 0;
let errorReportWindowStart = Date.now();
let hasRestoredFromStorage = false; // Track if we've already restored from storage this session
const ERROR_REPORT_LIMIT = 10;
const ERROR_REPORT_WINDOW_MS = 60_000; // 1 minute
const RATE_LIMIT_SESSION_KEY = '__telemetry_rate_limit_window';

// Load the persisted window from sessionStorage if available.
const getPersistedWindow = (): { startTime: number } | null => {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const stored = sessionStorage.getItem(RATE_LIMIT_SESSION_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed.startTime === 'number') {
      return parsed;
    }
  } catch {
    // Ignore parse errors; treat as no persisted window
  }
  return null;
};

// Persist the window to sessionStorage.
const persistWindow = (startTime: number): void => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(RATE_LIMIT_SESSION_KEY, JSON.stringify({ startTime }));
  } catch {
    // Ignore storage errors (e.g., quota exceeded); rate limiting continues to work in-memory
  }
};

// Clear the persisted window from sessionStorage.
const clearPersistedWindow = (): void => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(RATE_LIMIT_SESSION_KEY);
  } catch {
    // Ignore removal errors
  }
};

// Exported for unit testing (mirrors the build*ForTest convention in server.mjs).
export const isRateLimited = (): boolean => {
  const now = Date.now();

  // On first call or when window expires, check sessionStorage for a persisted window
  if (!hasRestoredFromStorage || now - errorReportWindowStart > ERROR_REPORT_WINDOW_MS) {
    hasRestoredFromStorage = true;

    const persisted = getPersistedWindow();
    if (persisted && now - persisted.startTime <= ERROR_REPORT_WINDOW_MS) {
      // Persisted window is still valid; restore it and assume quota is full (conservative)
      errorReportWindowStart = persisted.startTime;
      errorReportCount = ERROR_REPORT_LIMIT;
    } else {
      // No valid persisted window; start a new one
      errorReportCount = 0;
      errorReportWindowStart = now;
      clearPersistedWindow();
    }
  }

  // Persist the current window to protect against reloads
  persistWindow(errorReportWindowStart);

  if (errorReportCount >= ERROR_REPORT_LIMIT) {
    if (import.meta.env.DEV) {
      console.warn('[error-reporting] Rate limit exceeded, dropping error');
    }
    return true;
  }

  errorReportCount += 1;
  return false;
};

// Test-only: reset the rate-limit window so each test starts clean.
export const resetRateLimitForTest = (): void => {
  errorReportCount = 0;
  errorReportWindowStart = Date.now();
  hasRestoredFromStorage = false;
  clearPersistedWindow();
};

// Build error message with proper sanitization.
const buildMessage = (options: ReportErrorOptions): string => {
  if (options.source === 'api') {
    const method = options.api?.method?.toUpperCase() || 'REQUEST';
    const status = options.api?.status || 'network';
    return `FrontendApiError: ${method} ${getSafeApiUrl(options.api?.url) || 'api'} ${status}`;
  }

  const errorName = getErrorName(options.error);
  const stackTop = getSanitizedStackTop(options.error);
  const route = getSafeRoutePattern();

  return [
    `Frontend${errorName}: ${options.source} failure`,
    `route=${route}`,
    stackTop ? `at ${stackTop}` : 'at unavailable',
  ].join('\n');
};

// Build the error reporting payload (GCP Error Reporting format).
// Exported for unit testing (mirrors the build*ForTest convention in server.mjs).
export const buildPayload = (
  options: ReportErrorOptions
): ErrorReportingPayload => ({
  eventTime: new Date().toISOString(),
  serviceContext: {
    service: serviceName,
    version: serviceVersion,
  },
  message: buildMessage(options),
  context: {
    ...(options.source === 'api'
      ? {
          httpRequest: {
            method: options.api?.method?.toUpperCase(),
            url: getSafeApiUrl(options.api?.url),
            responseStatusCode: options.api?.status,
          },
        }
      : null),
    reportLocation: {
      filePath: getSafeRoutePattern(),
      functionName: options.source,
      lineNumber: 0,
    },
  },
});

// Determine if error reporting is active.
const shouldReport = (): boolean => isEnabled && Boolean(clientErrorsEndpoint);

// Main export: report a client error to the same-origin telemetry endpoint.
export const reportClientError = (options: ReportErrorOptions): void => {
  if (!shouldReport() || isRateLimited()) {
    if (import.meta.env.DEV) {
      console.info('[error-reporting]', buildPayload(options));
    }
    return;
  }

  void fetch(clientErrorsEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildPayload(options)),
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {
    // Error reporting must never affect app behavior.
  });
};

// Report API errors from Axios.
export const reportApiError = (error: unknown): void => {
  if (!axios.isAxiosError(error)) {
    reportClientError({ source: 'api', error });
    return;
  }

  const axiosError = error as AxiosError;

  reportClientError({
    source: 'api',
    error,
    api: {
      method: axiosError.config?.method,
      url: axiosError.config?.url,
      status: axiosError.response?.status,
    },
  });
};

// Report API failures (when we already have the HTTP details).
export const reportApiFailure = (api: {
  method?: string;
  url?: string;
  status?: number;
}): void => {
  reportClientError({
    source: 'api',
    api,
  });
};

// Initialize global error listeners (uncaught errors, unhandled promise rejections).
export const initializeGlobalErrorReporting = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', (event) => {
    reportClientError({
      source: 'runtime',
      error: event.error || event.message,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportClientError({
      source: 'unhandledrejection',
      error: event.reason,
    });
  });
};
